import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { authService, User, onAuthStateChange, LoginCredentials, RegisterData, AuthError } from '../services/authService';
import { validateEmail, validatePassword, validateName, validatePhone, sanitizeEmail } from '../utils/validation';
import { logError, logInfo } from '../utils/environment';

// ==================== TIPOS ====================

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  isEmailVerified: boolean;
  lastActivity: Date | null;
  sessionExpiresAt: Date | null;
}

export interface AuthContextType extends AuthState {
  // Autenticação básica
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string; userId?: string }>;
  logout: () => Promise<void>;
  
  // Recuperação de senha
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  updatePassword: (newPassword: string, currentPassword?: string) => Promise<{ success: boolean; error?: string }>;
  
  // Perfil
  updateProfile: (updates: Partial<User>) => Promise<{ success: boolean; error?: string }>;
  refreshUser: () => Promise<void>;
  
  // Verificação
  verifyEmail: (token?: string) => Promise<{ success: boolean; error?: string }>;
  resendVerificationEmail: () => Promise<{ success: boolean; error?: string }>;
  
  // Autenticação social
  loginWithGoogle: () => Promise<{ success: boolean; error?: string }>;
  loginWithFacebook: () => Promise<{ success: boolean; error?: string }>;
  loginWithApple: () => Promise<{ success: boolean; error?: string }>;
  
  // Sessão
  refreshSession: () => Promise<void>;
  clearError: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
  sessionTimeout?: number; // minutos
}

// ==================== CONTEXTO ====================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ==================== HOOK PERSONALIZADO ====================

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// ==================== PROVIDER ====================

export const AuthProvider = ({ children, sessionTimeout = 120 }: AuthProviderProps) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    loading: true,
    error: null,
    isAuthenticated: false,
    isEmailVerified: false,
    lastActivity: null,
    sessionExpiresAt: null,
  });

  // ==================== VERIFICAR SESSÃO INICIAL ====================
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        logInfo('Inicializando autenticação...');
        
        const currentUser = await authService.getCurrentUser();
        
        if (currentUser) {
          const expiresAt = new Date();
          expiresAt.setMinutes(expiresAt.getMinutes() + sessionTimeout);
          
          setState({
            user: currentUser,
            loading: false,
            error: null,
            isAuthenticated: true,
            isEmailVerified: currentUser.email_confirmed_at ? true : false,
            lastActivity: new Date(),
            sessionExpiresAt: expiresAt,
          });
          
          logInfo('Utilizador autenticado:', currentUser.email);
        } else {
          setState(prev => ({
            ...prev,
            loading: false,
          }));
        }
      } catch (error) {
        logError('Erro ao verificar sessão:', error);
        setState(prev => ({
          ...prev,
          loading: false,
          error: 'Erro ao verificar sessão',
        }));
      }
    };

    initializeAuth();

    // ==================== OUVIR MUDANÇAS DE AUTENTICAÇÃO ====================
    const { data: { subscription } } = onAuthStateChange((newUser) => {
      logInfo('Estado de autenticação alterado:', newUser?.email);
      
      if (newUser) {
        const expiresAt = new Date();
        expiresAt.setMinutes(expiresAt.getMinutes() + sessionTimeout);
        
        setState({
          user: newUser,
          loading: false,
          error: null,
          isAuthenticated: true,
          isEmailVerified: newUser.email_confirmed_at ? true : false,
          lastActivity: new Date(),
          sessionExpiresAt: expiresAt,
        });
      } else {
        setState({
          user: null,
          loading: false,
          error: null,
          isAuthenticated: false,
          isEmailVerified: false,
          lastActivity: null,
          sessionExpiresAt: null,
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [sessionTimeout]);

  // ==================== ATUALIZAR ATIVIDADE ====================
  useEffect(() => {
    if (!state.user) return;

    const updateActivity = () => {
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + sessionTimeout);
      
      setState(prev => ({
        ...prev,
        lastActivity: new Date(),
        sessionExpiresAt: expiresAt,
      }));
    };

    // Eventos que indicam atividade do utilizador
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [state.user, sessionTimeout]);

  // ==================== VERIFICAR EXPIRAÇÃO DA SESSÃO ====================
  useEffect(() => {
    if (!state.sessionExpiresAt || !state.user) return;

    const checkSession = setInterval(() => {
      const now = new Date();
      
      if (now >= state.sessionExpiresAt!) {
        logInfo('Sessão expirada');
        logout();
      }
    }, 60000); // Verificar a cada minuto

    return () => clearInterval(checkSession);
  }, [state.sessionExpiresAt, state.user]);

  // ==================== FUNÇÕES DE AUTENTICAÇÃO ====================

  const login = async (email: string, password: string) => {
    try {
      setState(prev => ({ ...prev, error: null, loading: true }));

      // Validações
      const emailValidation = validateEmail(email);
      if (!emailValidation.valid) {
        setState(prev => ({ ...prev, loading: false }));
        return { success: false, error: emailValidation.error };
      }

      const passwordValidation = validatePassword(password);
      if (!passwordValidation.valid) {
        setState(prev => ({ ...prev, loading: false }));
        return { success: false, error: passwordValidation.error };
      }

      const sanitizedEmail = sanitizeEmail(email);
      
      const { user: loggedUser, error } = await authService.login({
        email: sanitizedEmail,
        password,
      });

      if (error) {
        setState(prev => ({ ...prev, loading: false, error: error.message }));
        return { success: false, error: error.message };
      }

      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + sessionTimeout);

      setState({
        user: loggedUser,
        loading: false,
        error: null,
        isAuthenticated: true,
        isEmailVerified: loggedUser?.email_confirmed_at ? true : false,
        lastActivity: new Date(),
        sessionExpiresAt: expiresAt,
      });

      logInfo('Login bem-sucedido:', email);
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof AuthError ? error.message : 'Erro ao fazer login. Tente novamente.';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  const register = async (data: RegisterData) => {
    try {
      setState(prev => ({ ...prev, error: null, loading: true }));

      // Validações
      const emailValidation = validateEmail(data.email);
      if (!emailValidation.valid) {
        setState(prev => ({ ...prev, loading: false }));
        return { success: false, error: emailValidation.error };
      }

      const passwordValidation = validatePassword(data.password);
      if (!passwordValidation.valid) {
        setState(prev => ({ ...prev, loading: false }));
        return { success: false, error: passwordValidation.error };
      }

      const nameValidation = validateName(data.fullName, 'Nome completo');
      if (!nameValidation.valid) {
        setState(prev => ({ ...prev, loading: false }));
        return { success: false, error: nameValidation.error };
      }

      if (data.phone) {
        const phoneValidation = validatePhone(data.phone);
        if (!phoneValidation.valid) {
          setState(prev => ({ ...prev, loading: false }));
          return { success: false, error: phoneValidation.error };
        }
      }

      const sanitizedData = {
        ...data,
        email: sanitizeEmail(data.email),
      };

      const { user: newUser, error } = await authService.register(sanitizedData);

      if (error) {
        setState(prev => ({ ...prev, loading: false, error: error.message }));
        return { success: false, error: error.message };
      }

      setState(prev => ({
        ...prev,
        user: newUser,
        loading: false,
        error: null,
        isAuthenticated: true,
        isEmailVerified: false,
        lastActivity: new Date(),
        sessionExpiresAt: new Date(Date.now() + sessionTimeout * 60000),
      }));

      logInfo('Registo bem-sucedido:', data.email);
      return { success: true, userId: newUser?.id };
    } catch (error) {
      const errorMessage = error instanceof AuthError ? error.message : 'Erro ao criar conta. Tente novamente.';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  const logout = useCallback(async () => {
    try {
      await authService.logout();
      
      setState({
        user: null,
        loading: false,
        error: null,
        isAuthenticated: false,
        isEmailVerified: false,
        lastActivity: null,
        sessionExpiresAt: null,
      });

      logInfo('Logout realizado com sucesso');
    } catch (error) {
      logError('Erro ao fazer logout:', error);
    }
  }, []);

  const resetPassword = async (email: string) => {
    try {
      setState(prev => ({ ...prev, error: null }));

      const emailValidation = validateEmail(email);
      if (!emailValidation.valid) {
        return { success: false, error: emailValidation.error };
      }

      const sanitizedEmail = sanitizeEmail(email);
      
      const { error } = await authService.resetPassword(sanitizedEmail);

      if (error) {
        setState(prev => ({ ...prev, error: error.message }));
        return { success: false, error: error.message };
      }

      logInfo('Email de recuperação enviado:', email);
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof AuthError ? error.message : 'Erro ao enviar email de recuperação.';
      setState(prev => ({ ...prev, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  const updatePassword = async (newPassword: string, currentPassword?: string) => {
    try {
      setState(prev => ({ ...prev, error: null }));

      const passwordValidation = validatePassword(newPassword);
      if (!passwordValidation.valid) {
        return { success: false, error: passwordValidation.error };
      }

      const { error } = await authService.updatePassword(newPassword, currentPassword);

      if (error) {
        setState(prev => ({ ...prev, error: error.message }));
        return { success: false, error: error.message };
      }

      logInfo('Senha atualizada com sucesso');
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof AuthError ? error.message : 'Erro ao atualizar senha.';
      setState(prev => ({ ...prev, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  const updateProfile = async (updates: Partial<User>) => {
    try {
      setState(prev => ({ ...prev, error: null, loading: true }));

      if (updates.full_name) {
        const nameValidation = validateName(updates.full_name, 'Nome completo');
        if (!nameValidation.valid) {
          setState(prev => ({ ...prev, loading: false }));
          return { success: false, error: nameValidation.error };
        }
      }

      if (updates.phone) {
        const phoneValidation = validatePhone(updates.phone);
        if (!phoneValidation.valid) {
          setState(prev => ({ ...prev, loading: false }));
          return { success: false, error: phoneValidation.error };
        }
      }

      const { error } = await authService.updateProfile(updates);

      if (error) {
        setState(prev => ({ ...prev, loading: false, error: error.message }));
        return { success: false, error: error.message };
      }

      // Atualizar utilizador local
      if (state.user) {
        const updatedUser = {
          ...state.user,
          ...updates,
        };

        setState(prev => ({
          ...prev,
          user: updatedUser,
          loading: false,
        }));
      }

      logInfo('Perfil atualizado com sucesso');
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof AuthError ? error.message : 'Erro ao atualizar perfil.';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      
      setState(prev => ({
        ...prev,
        user: currentUser,
        isEmailVerified: currentUser?.email_confirmed_at ? true : false,
      }));
    } catch (error) {
      logError('Erro ao atualizar utilizador:', error);
    }
  }, []);

  const verifyEmail = async (token?: string) => {
    try {
      setState(prev => ({ ...prev, error: null, loading: true }));

      const { error } = await authService.verifyEmail(token);

      if (error) {
        setState(prev => ({ ...prev, loading: false, error: error.message }));
        return { success: false, error: error.message };
      }

      await refreshUser();

      setState(prev => ({
        ...prev,
        loading: false,
        isEmailVerified: true,
      }));

      logInfo('Email verificado com sucesso');
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof AuthError ? error.message : 'Erro ao verificar email.';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  const resendVerificationEmail = async () => {
    try {
      setState(prev => ({ ...prev, error: null }));

      if (!state.user?.email) {
        return { success: false, error: 'Email não encontrado' };
      }

      const { error } = await authService.resendVerificationEmail(state.user.email);

      if (error) {
        setState(prev => ({ ...prev, error: error.message }));
        return { success: false, error: error.message };
      }

      logInfo('Email de verificação reenviado');
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof AuthError ? error.message : 'Erro ao reenviar verificação.';
      setState(prev => ({ ...prev, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  const loginWithGoogle = async () => {
    try {
      setState(prev => ({ ...prev, error: null, loading: true }));

      const { user, error } = await authService.loginWithGoogle();

      if (error) {
        setState(prev => ({ ...prev, loading: false, error: error.message }));
        return { success: false, error: error.message };
      }

      if (user) {
        setState({
          user,
          loading: false,
          error: null,
          isAuthenticated: true,
          isEmailVerified: user.email_confirmed_at ? true : false,
          lastActivity: new Date(),
          sessionExpiresAt: new Date(Date.now() + sessionTimeout * 60000),
        });
      }

      logInfo('Login com Google bem-sucedido');
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof AuthError ? error.message : 'Erro ao fazer login com Google.';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  const loginWithFacebook = async () => {
    try {
      setState(prev => ({ ...prev, error: null, loading: true }));

      const { user, error } = await authService.loginWithFacebook();

      if (error) {
        setState(prev => ({ ...prev, loading: false, error: error.message }));
        return { success: false, error: error.message };
      }

      if (user) {
        setState({
          user,
          loading: false,
          error: null,
          isAuthenticated: true,
          isEmailVerified: user.email_confirmed_at ? true : false,
          lastActivity: new Date(),
          sessionExpiresAt: new Date(Date.now() + sessionTimeout * 60000),
        });
      }

      logInfo('Login com Facebook bem-sucedido');
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof AuthError ? error.message : 'Erro ao fazer login com Facebook.';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  const loginWithApple = async () => {
    try {
      setState(prev => ({ ...prev, error: null, loading: true }));

      const { user, error } = await authService.loginWithApple();

      if (error) {
        setState(prev => ({ ...prev, loading: false, error: error.message }));
        return { success: false, error: error.message };
      }

      if (user) {
        setState({
          user,
          loading: false,
          error: null,
          isAuthenticated: true,
          isEmailVerified: user.email_confirmed_at ? true : false,
          lastActivity: new Date(),
          sessionExpiresAt: new Date(Date.now() + sessionTimeout * 60000),
        });
      }

      logInfo('Login com Apple bem-sucedido');
      return { success: true };
    } catch (error) {
      const errorMessage = error instanceof AuthError ? error.message : 'Erro ao fazer login com Apple.';
      setState(prev => ({ ...prev, loading: false, error: errorMessage }));
      return { success: false, error: errorMessage };
    }
  };

  const refreshSession = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      
      if (currentUser) {
        const expiresAt = new Date();
        expiresAt.setMinutes(expiresAt.getMinutes() + sessionTimeout);
        
        setState(prev => ({
          ...prev,
          user: currentUser,
          lastActivity: new Date(),
          sessionExpiresAt: expiresAt,
        }));
      }
    } catch (error) {
      logError('Erro ao renovar sessão:', error);
    }
  };

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    resetPassword,
    updatePassword,
    updateProfile,
    refreshUser,
    verifyEmail,
    resendVerificationEmail,
    loginWithGoogle,
    loginWithFacebook,
    loginWithApple,
    refreshSession,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== HOOKS AUXILIARES ====================

export const useRequireAuth = (redirectTo?: string) => {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.loading && !auth.isAuthenticated && redirectTo) {
      navigate(redirectTo);
    }
  }, [auth.loading, auth.isAuthenticated, redirectTo, navigate]);

  return auth;
};

export const useRequireGuest = (redirectTo?: string) => {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.loading && auth.isAuthenticated && redirectTo) {
      navigate(redirectTo);
    }
  }, [auth.loading, auth.isAuthenticated, redirectTo, navigate]);

  return auth;
};

export const useRequireEmailVerified = (redirectTo?: string) => {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.loading && auth.isAuthenticated && !auth.isEmailVerified && redirectTo) {
      navigate(redirectTo);
    }
  }, [auth.loading, auth.isAuthenticated, auth.isEmailVerified, redirectTo, navigate]);

  return auth;
};

// Import necessário para o useNavigate
import { useNavigate } from 'react-router-dom';